import React, { useState, useEffect, useCallback } from 'react';
import {
  Crown,
  Check,
  Zap,
  Shield,
  CreditCard,
  Calendar,
  ArrowRight,
  Sparkles,
  Loader2,
  Download,
  Mail,
  Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI, api } from '../lib/api';
import { toast } from 'sonner';

const PLAN_VISUAL = {
  starter: { icon: Zap, color: 'blue', tagline: 'Voor wie net begint met online bestellen' },
  enterprise: { icon: Shield, color: 'purple', tagline: 'Maatwerk voor ketens en complexe setups' },
};

const formatEUR = (n) => `€${(Math.round(n * 100) / 100).toFixed(2)}`;

export const SubscriptionPage = () => {
  const { restaurant, refreshAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState('starter');
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await api.get('/subscriptions/invoices');
      setInvoices(res.data?.invoices || []);
    } catch (e) {
      // Silent — empty list is fine for new accounts
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch live plan list from backend (so prices stay in sync with server)
    api.get('/subscriptions/plans')
      .then(res => setPlans(res.data?.plans || []))
      .catch(() => setPlans([]));
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    // Defer state updates to next microtask to satisfy react-hooks rules
    const t = setTimeout(() => {
      if (restaurant?.subscription_plan) {
        setCurrentPlan(restaurant.subscription_plan);
      }
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const success = urlParams.get('success');
      const cancelled = urlParams.get('cancelled');
      if (sessionId && success === 'true') {
        checkPaymentStatus(sessionId);
      } else if (cancelled) {
        toast.error('Betaling geannuleerd');
        window.history.replaceState({}, '', window.location.pathname);
      }
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    try {
      let attempts = 0;
      const poll = async () => {
        const response = await subscriptionAPI.getStatus(sessionId);
        if (response.data.payment_status === 'paid') {
          toast.success('Abonnement succesvol geactiveerd!');
          if (refreshAuth) await refreshAuth();
          await loadInvoices();
          window.history.replaceState({}, '', window.location.pathname);
          setCheckingPayment(false);
          return;
        }
        attempts++;
        if (attempts < 10) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Betaling wordt verwerkt. Vernieuw de pagina over enkele seconden.');
          setCheckingPayment(false);
        }
      };
      await poll();
    } catch (error) {
      toast.error('Kon betalingsstatus niet controleren');
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = (plan) => {
    if (plan.contact_sales) {
      // Enterprise — open mailto
      window.location.href = `mailto:sales@food-flows.com?subject=Enterprise%20aanvraag%20-%20${encodeURIComponent(restaurant?.name || '')}&body=Hallo%20FoodFlows%2C%0A%0AIk%20wil%20graag%20een%20offerte%20voor%20een%20Enterprise%20abonnement.%0A%0ARestaurant%3A%20${encodeURIComponent(restaurant?.name || '')}%0ALocaties%3A%20%0AAantal%20bestellingen%2Fmaand%3A%20%0ASpeciale%20wensen%3A%20%0A%0ABedankt%21`;
      return;
    }
    setSelectedPlan({ ...plan });
    setShowUpgradeDialog(true);
  };

  const confirmUpgrade = async () => {
    if (!selectedPlan) return;
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await subscriptionAPI.createCheckout(selectedPlan.id, 'monthly', originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Kon checkout niet starten. Probeer opnieuw.');
      setLoading(false);
    }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const res = await api.get(`/subscriptions/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factuur-${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Kon factuur niet downloaden');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-gray-900">Abonnement</h1>
        <p className="text-gray-500">Beheer je abonnement en download je facturen</p>
      </div>

      {/* Current Plan banner */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-white/80 text-sm">Huidig abonnement</p>
            <h2 className="text-2xl font-bold capitalize" data-testid="current-plan-name">{currentPlan}</h2>
            <p className="text-white/80 mt-1 text-sm">
              {restaurant?.subscription_end_date
                ? `Verlengt automatisch op ${formatDate(restaurant.subscription_end_date)}`
                : 'Maandelijks - automatisch verlengbaar'}
            </p>
          </div>
          <Crown className="w-12 h-12 text-white/30" />
        </div>
      </div>

      {/* Plans grid - 3 tiers */}
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const visual = PLAN_VISUAL[plan.id] || { icon: Star, color: 'gray', tagline: '' };
          const PlanIcon = visual.icon;
          const isCurrentPlan = currentPlan === plan.id;
          const isContactSales = plan.contact_sales || plan.price === null;

          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-2xl border-2 transition-all bg-white ${
                visual.popular
                  ? 'border-emerald-500 shadow-xl md:scale-105'
                  : isCurrentPlan
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {visual.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-emerald-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Populairst
                  </span>
                </div>
              )}

              <div className="p-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-${visual.color}-100`}>
                  <PlanIcon className={`w-6 h-6 text-${visual.color}-600`} />
                </div>

                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                <p className="text-gray-500 text-sm mb-4 min-h-[2.5em]">{visual.tagline}</p>

                <div className="mb-6">
                  {isContactSales ? (
                    <>
                      <span className="text-2xl font-bold text-gray-900">Op aanvraag</span>
                      <p className="text-sm text-gray-500 mt-1">Op maat geprijsd</p>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-gray-900">{formatEUR(plan.price)}</span>
                      <span className="text-gray-500">/maand</span>
                      <p className="text-xs text-gray-400 mt-1">Incl. {Math.round((plan.vat_rate || 0.12) * 100)}% BTW</p>
                    </>
                  )}
                </div>

                <ul className="space-y-2.5 mb-6 min-h-[280px]">
                  {(plan.features || []).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <Button className="w-full" disabled variant="outline" data-testid={`plan-current-${plan.id}`}>
                    Huidig Plan
                  </Button>
                ) : isContactSales ? (
                  <Button
                    className={`w-full ${visual.popular ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                    variant={visual.popular ? 'default' : 'outline'}
                    onClick={() => handleUpgrade(plan)}
                    data-testid={`plan-cta-${plan.id}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Neem Contact Op
                  </Button>
                ) : (
                  <Button
                    className={`w-full ${visual.popular ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                    variant={visual.popular ? 'default' : 'outline'}
                    onClick={() => handleUpgrade(plan)}
                    data-testid={`plan-cta-${plan.id}`}
                  >
                    Kies {plan.name}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Invoices / Billing history — REAL data from backend */}
      <div className="card-dashboard">
        <h3 className="font-heading font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#1A4D2E]" />
          Facturen
        </h3>

        {invoicesLoading ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 text-emerald-500 animate-spin mx-auto" />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-gray-500 text-center py-8" data-testid="no-invoices">
            Nog geen facturen — zodra je een abonnement afsluit, vind je hier al je betaalde facturen.
          </p>
        ) : (
          <div className="divide-y" data-testid="invoices-list">
            <div className="grid grid-cols-12 gap-2 pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-3">Factuur</div>
              <div className="col-span-3">Plan</div>
              <div className="col-span-2">Datum</div>
              <div className="col-span-2 text-right">Bedrag</div>
              <div className="col-span-2 text-right">Actie</div>
            </div>
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="grid grid-cols-12 gap-2 py-3 items-center"
                data-testid={`invoice-row-${inv.id}`}
              >
                <div className="col-span-3 flex items-center gap-3">
                  <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="font-mono text-sm font-medium text-gray-900">{inv.invoice_number}</span>
                </div>
                <div className="col-span-3 text-sm text-gray-700">{inv.plan_name}</div>
                <div className="col-span-2 text-sm text-gray-500">{formatDate(inv.paid_at)}</div>
                <div className="col-span-2 text-right">
                  <span className="font-medium text-gray-900">{formatEUR(inv.amount_total)}</span>
                  <span className="block text-xs text-gray-400">incl. BTW</span>
                </div>
                <div className="col-span-2 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadInvoicePdf(inv)}
                    data-testid={`download-invoice-${inv.id}`}
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Kies {selectedPlan?.name}</DialogTitle>
          </DialogHeader>

          {selectedPlan && (
            <div className="py-4 space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-900">
                      {selectedPlan.name} - {formatEUR(selectedPlan.price)}/maand
                    </p>
                    <p className="text-sm text-emerald-700">Incl. {Math.round((selectedPlan.vat_rate || 0.12) * 100)}% BTW</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Je wordt doorgestuurd naar onze beveiligde betaalpagina (Stripe). Je abonnement wordt na succesvolle
                betaling direct geactiveerd.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)} disabled={loading}>
              Annuleren
            </Button>
            <Button className="btn-primary" onClick={confirmUpgrade} disabled={loading} data-testid="confirm-upgrade-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Even geduld...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Doorgaan naar Betaling
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4">
            <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Betaling wordt verwerkt</h3>
            <p className="text-gray-500">Even geduld, we activeren je abonnement...</p>
          </div>
        </div>
      )}
    </div>
  );
};
